import React, { useState } from "react";
import { Input, Select } from "antd";

import AssigneePicker from "./AssigneePicker";
import EpicLink from "./EpicLink";
import LabelsPicker from "./LabelsPicker";
import { ISSUE_TYPES } from "../../constants/projects.constants";
import type { IssueType, Label, Status } from "../../../../core/data/redux/projects/types";

export interface IssueListFilters {
  q: string;
  type: IssueType | null;
  status_id: number | null;
  assignee_id: number | null;
  epic_id: number | null;
  label_ids: number[];
}

interface IssueFiltersProps {
  filters: IssueListFilters;
  statuses: Status[];
  labels: Label[];
  projectId: number | null | undefined;
  onFilterChange: (partial: Partial<IssueListFilters>) => void;
}

const H = 36;

const selectStyle: React.CSSProperties = {
  height: H,
};

const IssueFilters: React.FC<IssueFiltersProps> = ({
  filters,
  statuses,
  labels,
  projectId,
  onFilterChange,
}) => {
  const [localSearch, setLocalSearch] = useState(filters.q);

  const activeFiltersCount = [
    filters.q,
    filters.type,
    filters.status_id,
    filters.assignee_id,
    filters.epic_id,
    filters.label_ids.length > 0 ? true : null,
  ].filter(Boolean).length;

  const handleSearch = () => onFilterChange({ q: localSearch.trim() });

  const handleReset = () => {
    setLocalSearch("");
    onFilterChange({ q: "", type: null, status_id: null, assignee_id: null, epic_id: null, label_ids: [] });
  };

  return (
    <div
      className="card mb-3 border-0"
      style={{ borderRadius: 12, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}
    >
      <div
        className="card-body bg-white rounded-3"
        style={{ padding: "12px 16px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}
      >

        {/* ── Search ── */}
        <div style={{ display: "flex", gap: 0, flexShrink: 0, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
          <Input
            placeholder="Search by title or key…"
            value={localSearch}
            onChange={(e) => {
              setLocalSearch(e.target.value);
              if (e.target.value === "") onFilterChange({ q: "" });
            }}
            onPressEnter={handleSearch}
            allowClear
            variant="borderless"
            style={{ width: 210, height: H, paddingLeft: 10 }}
          />
          <button
            type="button"
            onClick={handleSearch}
            style={{
              height: H,
              width: 38,
              flexShrink: 0,
              background: "#3b82f6",
              border: "none",
              borderLeft: "1px solid #2563eb",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#2563eb")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#3b82f6")}
          >
            <i className="ti ti-search" style={{ fontSize: 15, color: "#fff" }} />
          </button>
        </div>

        {/* ── Divider ── */}
        <div style={{ width: 1, height: 24, background: "#e2e8f0", flexShrink: 0 }} />

        {/* ── Type ── */}
        <Select
          placeholder={
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <i className="ti ti-tag" style={{ fontSize: 14, color: "#94a3b8" }} />
              Type
            </span>
          }
          allowClear
          style={{ ...selectStyle, width: 130 }}
          value={filters.type ?? undefined}
          onChange={(v) => onFilterChange({ type: (v as IssueType) ?? null })}
          options={ISSUE_TYPES.map((t) => ({ value: t.value, label: t.label }))}
        />

        {/* ── Status ── */}
        <Select
          placeholder={
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <i className="ti ti-circle-half-2" style={{ fontSize: 14, color: "#94a3b8" }} />
              Status
            </span>
          }
          allowClear
          style={{ ...selectStyle, width: 148 }}
          value={filters.status_id ?? undefined}
          onChange={(v) => onFilterChange({ status_id: v ?? null })}
          options={statuses
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((s) => ({ value: s.id, label: s.name }))}
        />

        {/* ── Assignee ── */}
        <div style={{ width: 180 }}>
          <AssigneePicker
            value={filters.assignee_id}
            onChange={(uid) => onFilterChange({ assignee_id: uid })}
            size="middle"
          />
        </div>

        {/* ── Epic ── */}
        <div style={{ width: 180 }}>
          <EpicLink
            projectId={projectId}
            value={filters.epic_id}
            onChange={(eid) => onFilterChange({ epic_id: eid })}
          />
        </div>

        {/* ── Labels ── */}
        <div style={{ width: 200 }}>
          <LabelsPicker
            labels={labels}
            value={filters.label_ids}
            onChange={(ids) => onFilterChange({ label_ids: ids })}
          />
        </div>

        {/* ── Reset ── */}
        <button
          type="button"
          onClick={handleReset}
          className="btn btn-sm ms-auto position-relative"
          style={{
            height: H,
            paddingInline: 14,
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: activeFiltersCount > 0 ? "#fff7ed" : "#f8fafc",
            color: activeFiltersCount > 0 ? "#f59e0b" : "#94a3b8",
            border: `1px solid ${activeFiltersCount > 0 ? "#fcd34d" : "#e2e8f0"}`,
            transition: "all 0.15s",
          }}
        >
          <i className="ti ti-filter-off" style={{ fontSize: 15 }} />
          Reset
          {activeFiltersCount > 0 && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "#f59e0b",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default IssueFilters;